import { leftOuterJoin } from "../../common/utilities/LinqExtensions";
import { deviceRoutes, groupRoutes, IRoute, tagRoutes, tankBatteryRoutes, tankRoutes, wellRoutes } from "../../routes/routes";
import {
    IUICustomizationNavigationDrawerSettingsDTO,
    IUICustomizationSettingsDTO,
    IUICustomizationViewCategorySettingsDTO,
    IUICustomizationViewSettingsDTO
} from "../../services/ui-customization-data-service/Models";
import {
    UICustomizationFieldType,
    UICustomizationNavigationDrawerSettingsFieldIsDirtyType,
    UICustomizationNavigationDrawerSettingsFieldType,
    UICustomizationSettingsFieldIsDirtyType,
    UICustomizationSettingsFieldType,
    UICustomizationViewCategoryListSettingsFieldIsDirtyType,
    UICustomizationViewCategoryListSettingsFieldType,
    UICustomizationViewCategorySettingsFieldIsDirtyType,
    UICustomizationViewCategorySettingsFieldType,
    UICustomizationViewSettingsFieldIsDirtyType,
    UICustomizationViewSettingsFieldType
} from "./UICustomizationSettingsFieldTypes";

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Merge Functions

export function mergeUICustomizationSettings(
    customizationSettingsField: UICustomizationSettingsFieldType | undefined
): IUICustomizationSettingsDTO | undefined {
    const merged = customizationSettingsField
        ? {
            ...customizationSettingsField,
            navigationDrawer: mergeUICustomizationNavigationDrawerSettings(customizationSettingsField)
        }
        : undefined;

    return merged;
}

export function mergeUICustomizationNavigationDrawerSettings(
    customizationSettingsField: UICustomizationSettingsFieldType
): IUICustomizationNavigationDrawerSettingsDTO {
    return {
        ...customizationSettingsField?.navigationDrawer,
        viewCategories: {
            group: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.group),
            device: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.device),
            well: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.well),
            tankBattery: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.tankBattery),
            tank: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.tank),
            tag: mergeUICustomizationViewCategorySettings(customizationSettingsField?.navigationDrawer.viewCategories.tag),
        }
    };
}

export function mergeUICustomizationViewCategorySettings(
    field: UICustomizationViewCategorySettingsFieldType
): IUICustomizationViewCategorySettingsDTO {
    return {
        ungrouped: field.ungrouped,
        views: field.views.map((v, index) => mergeUICustomizationViewSettingsFieldType(v, index))
    };
}

export function mergeUICustomizationViewSettingsFieldType(
    field: UICustomizationViewSettingsFieldType, index: number
): IUICustomizationViewSettingsDTO {
    return {
        viewID: field.viewID,
        hidden: field.hidden,
        order: index
    };
}

// #endregion Merge Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region Field conversion functions

export function toUICustomizationField(
    dto: IUICustomizationSettingsDTO | undefined
): UICustomizationFieldType {

    return {
        customizationSettings: toUICustomizationSettingsField(dto)
    };
}

export function toUICustomizationSettingsField(
    dto: IUICustomizationSettingsDTO | undefined
): UICustomizationSettingsFieldType {

    const viewCategories = dto?.navigationDrawer.viewCategories;

    return {
        navigationDrawer: {
            openOnHover: dto?.navigationDrawer.openOnHover ?? false,
            viewCategories: {
                group: {
                    ungrouped: viewCategories?.group.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.group.views ?? [],
                        groupRoutes
                    )
                },
                device: {
                    ungrouped: viewCategories?.device.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.device.views ?? [],
                        deviceRoutes
                    )
                },
                well: {
                    ungrouped: viewCategories?.well.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.well?.views ?? [],
                        wellRoutes
                    )
                },
                tankBattery: {
                    ungrouped: viewCategories?.tankBattery.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.tankBattery?.views ?? [],
                        tankBatteryRoutes
                    )
                },
                tank: {
                    ungrouped: viewCategories?.tank.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.tank?.views ?? [],
                        tankRoutes
                    )
                },
                tag: {
                    ungrouped: viewCategories?.tag.ungrouped ?? false,
                    views: toUICustomizationViewSettingsFields(
                        viewCategories?.tag?.views ?? [],
                        tagRoutes
                    )
                }
            }
        }
    };
}

function toUICustomizationViewSettingsFields(
    dto: Array<IUICustomizationViewSettingsDTO>,
    routes: Array<IRoute>
): Array<UICustomizationViewSettingsFieldType> {

    return leftOuterJoin(
        routes,
        dto,
        o => o.viewID!,
        i => i.viewID,
        (o, i) => ({
            viewID: o.viewID!,
            hidden: i ? i.hidden : false,
            order: i?.order ?? o.order,
            name: o.name,
            category: o.category,
            subCategory: o.subCategory,
            customizationDisabled: o.customizationDisabled
        })
    )
        .orderBy(e => e.order)
        .toArray();
}

// #endregion Field conversion functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region IsDirty Functions

export function customizationSettingsFieldIsDirty(
    dirtyFields?: UICustomizationSettingsFieldIsDirtyType
): boolean {
    return (
        customizationNavigationDrawerSettingsFieldIsDirty(dirtyFields?.navigationDrawer)
    ) ?? false;
}

export function customizationNavigationDrawerSettingsFieldIsDirty(
    dirtyFields?: UICustomizationNavigationDrawerSettingsFieldIsDirtyType
): boolean {
    return (
        dirtyFields?.openOnHover ||
        customizationViewCategoryListSettingsFieldIsDirty(dirtyFields?.viewCategories)
    ) ?? false;
}

export function customizationViewCategoryListSettingsFieldIsDirty(
    dirtyFields?: UICustomizationViewCategoryListSettingsFieldIsDirtyType
): boolean {
    return (
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.group) ||
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.device) ||
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.well) ||
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.tankBattery) ||
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.tank) ||
        customizationViewCategorySettingsFieldIsDirty(dirtyFields?.tag)
    ) ?? false;
}

export function customizationViewCategorySettingsFieldIsDirty(
    dirtyFields?: UICustomizationViewCategorySettingsFieldIsDirtyType
): boolean {
    return (
        dirtyFields?.ungrouped || (
            Array.isArray(dirtyFields?.views)
                ? dirtyFields?.views?.some(v => customizationViewSettingsFieldIsDirty(v))
                : dirtyFields?.views
        )
    ) ?? false;
}

export function customizationViewSettingsFieldIsDirty(
    dirtyFields?: UICustomizationViewSettingsFieldIsDirtyType
): boolean {
    return (
        dirtyFields?.viewID ||
        dirtyFields?.hidden ||
        dirtyFields?.order ||
        dirtyFields?.name ||
        dirtyFields?.category ||
        dirtyFields?.subCategory
    ) ?? false;
}

// #endregion IsDirty Functions

///////////////////////////////////////////////////////////////////////////////////////////////
// #region IsValid Functions

export function customizationSettingsFieldIsValid(
    dto?: UICustomizationSettingsFieldType
): boolean {
    return (
        customizationNavigationDrawerSettingsFieldIsValid(dto?.navigationDrawer)
    ) ?? false;
}

export function customizationNavigationDrawerSettingsFieldIsValid(
    dto?: UICustomizationNavigationDrawerSettingsFieldType
): boolean {
    return (
        customizationViewCategoryListSettingsFieldIsValid(dto?.viewCategories)
    ) ?? false;
}

export function customizationViewCategoryListSettingsFieldIsValid(
    dto?: UICustomizationViewCategoryListSettingsFieldType
): boolean {
    return (
        customizationViewCategorySettingsFieldIsValid(dto?.group) &&
        customizationViewCategorySettingsFieldIsValid(dto?.device) &&
        customizationViewCategorySettingsFieldIsValid(dto?.well) &&
        customizationViewCategorySettingsFieldIsValid(dto?.tankBattery) &&
        customizationViewCategorySettingsFieldIsValid(dto?.tank) &&
        customizationViewCategorySettingsFieldIsValid(dto?.tag)
    ) ?? false;
}

export function customizationViewCategorySettingsFieldIsValid(
    dto?: UICustomizationViewCategorySettingsFieldType
): boolean {
    return (
        dto?.views?.some(v => customizationViewSettingsFieldIsValid(v))
    ) ?? false;
}

export function customizationViewSettingsFieldIsValid(
    dto?: UICustomizationViewSettingsFieldType
): boolean {
    return !dto?.hidden;
}

// #endregion IsValid Functions